import {
  arrow,
  autoUpdate,
  flip,
  Middleware,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react'
import mergeRefs from 'react-merge-refs'
import cn from 'classnames'
import { CSSTransition } from 'react-transition-group'

import { stackingOrder } from 'src/shared/const/stackingOrder'
import { Portal } from 'src/shared/ui/Portal'
import { Stack } from 'src/shared/ui/Stack'

import styles from './index.module.css'
import { CommonPopoverProps, RefElement } from './typing'

const transitionClassNames = {
  enter: styles.enter,
  enterActive: styles.enterActive,
  exit: styles.exit,
  exitActive: styles.exitActive,
}

export const Popover = forwardRef<HTMLDivElement, CommonPopoverProps>(
  (
    {
      children,
      open,
      onOpenChange,
      anchorElement,
      useAnchorWidth,
      floatingClassName,
      position = 'bottom',
      offset: computedOffset = 5,
      withArrow = false,
      container,
      transitionProps,
      fallbackPosition,
      preventFlip = false,
      preventOverflow = false,
      zIndex = stackingOrder.POPOVER,
    },
    ref,
  ) => {
    const [referenceElement, setReferenceElement] = useState<RefElement>(null)
    const [floatingElement, setFloatingElement] = useState<RefElement>(null)
    const [arrowElement, setArrowElement] = useState<RefElement>(null)

    const floatingRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)

    const middleware = useMemo(() => {
      const modifiers: Middleware[] = [offset(computedOffset)]

      if (!preventFlip) {
        modifiers.push(
          flip({
            fallbackPlacements: fallbackPosition,
          }),
        )
      }

      if (!preventOverflow) {
        modifiers.push(shift({ padding: 5 }))
      }

      if (withArrow) {
        modifiers.push(
          arrow({
            element: arrowElement,
          }),
        )
      }

      return modifiers
    }, [offset, preventFlip, preventOverflow, withArrow])

    const { refs, context, floatingStyles } = useFloating({
      open,
      onOpenChange,
      placement: position,
      middleware,
      whileElementsMounted: autoUpdate,
      elements: {
        reference: referenceElement,
        floating: floatingElement,
      },
    })

    const interactions = useInteractions([useDismiss(context), useRole(context)])

    useLayoutEffect(() => {
      if (anchorElement) setReferenceElement(anchorElement)
    }, [anchorElement])

    const renderContent = (computedZIndex: number) => (
      <div
        ref={mergeRefs([ref, refs.setFloating, setFloatingElement])}
        className={styles.component}
        style={{
          zIndex: computedZIndex,
          width: useAnchorWidth ? referenceElement?.offsetWidth : undefined,
          ...floatingStyles,
        }}
        {...interactions.getFloatingProps()}
      >
        <div ref={innerRef} className={cn(styles.inner, floatingClassName)}>
          {children}
          {withArrow && <div ref={setArrowElement} className={styles.arrow} />}
        </div>
      </div>
    )

    return (
      <Stack value={zIndex}>
        {(computedZIndex: number) => (
          <Portal getPortalContainer={container}>
            <CSSTransition
              timeout={300}
              classNames={transitionClassNames}
              unmountOnExit
              nodeRef={floatingRef}
              {...transitionProps}
              in={open}
            >
              {renderContent(computedZIndex)}
            </CSSTransition>
          </Portal>
        )}
      </Stack>
    )
  },
)

Popover.displayName = 'Popover'
